import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  outreachDraftContentSchema,
  type OutreachDraftContent,
  type OutreachTestSendRequest,
} from '@aegislab/shared';
import {
  fetchOutreachDraft,
  fetchStibeeLists,
  saveOutreachDraft,
  sendOutreachTestMail,
} from '../api/outreachDraftApi';

/**
 * 초안은 캠페인 × 나라 × 작성자 하나다. 나라를 키에 넣어야 US 초안이 JP 초안을 덮지 않는다
 * (2026-09-18 결정: 발송 초안 하나 = 나라 하나).
 */
export const outreachDraftKeys = {
  all: ['outreach-draft'] as const,
  one: (scenarioId: string, country: string) => ['outreach-draft', scenarioId, country] as const,
  stibeeLists: (scenarioId: string) => ['outreach-draft', scenarioId, 'stibee-lists'] as const,
};

/** 주소록 목록 — 마이그레이션으로만 바뀌므로 창을 여는 동안은 다시 부르지 않는다. */
export function useStibeeLists(scenarioId: string) {
  return useQuery({
    queryKey: outreachDraftKeys.stibeeLists(scenarioId),
    queryFn: () => fetchStibeeLists(scenarioId),
    staleTime: Infinity,
  });
}

export function useOutreachDraft(scenarioId: string, country: string | null) {
  return useQuery({
    queryKey: outreachDraftKeys.one(scenarioId, country ?? ''),
    queryFn: () => fetchOutreachDraft(scenarioId, country as string),
    enabled: country !== null,
    staleTime: 30_000,
  });
}

export function useSaveOutreachDraft(scenarioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { country: string; content: OutreachDraftContent }) =>
      saveOutreachDraft(scenarioId, input),
    onSuccess: (row) => {
      queryClient.setQueryData(outreachDraftKeys.one(scenarioId, row.country), row);
    },
  });
}

export function useSendOutreachTestMail(scenarioId: string, country: string) {
  return useMutation({
    mutationFn: (body: OutreachTestSendRequest) => sendOutreachTestMail(scenarioId, country, body),
  });
}

/** 자동 저장 간격 — 타이핑이 멈춘 뒤 이만큼 지나면 보낸다. */
const AUTOSAVE_DELAY_MS = 1200;

/**
 * 초안 자동 저장 (IA §3-5 "개인 초안의 본문·이미지 편집값·링크를 자동 저장한다").
 *
 * 첫 렌더에는 보내지 않는다 — 서버에서 막 읽어 온 값을 그대로 되쓰면 저장 시각만 흔들린다.
 * 첫 값이 서버에 없는 것이면(`persistInitial` — 템플릿을 복사해 채운 새 초안) 그 값도 보낸다.
 * 손대지 않고 발송 확인으로 넘어가면 확인 단계가 서버의 빈 줄을 읽기 때문이다(2026-09-30).
 * 확정한 발송·예약본은 이 경로를 타지 않는다(초안 상태에서만 자동 저장, gbrain fact #1590).
 */
export function useOutreachDraftAutosave(
  scenarioId: string,
  country: string | null,
  content: OutreachDraftContent | null,
  options: { enabled: boolean; persistInitial?: boolean }
) {
  const save = useSaveOutreachDraft(scenarioId);
  const lastSaved = useRef<string | null>(null);
  const { mutate } = save;

  useEffect(() => {
    if (!options.enabled || country === null || content === null) return;
    const serialized = JSON.stringify(content);
    if (lastSaved.current === null && !options.persistInitial) {
      // 읽어 온 값 그대로는 저장하지 않는다.
      lastSaved.current = serialized;
      return;
    }
    if (lastSaved.current === serialized) return;
    /*
      **서버가 거부할 값은 보내지 않는다.** 타이핑이 멈추면 그대로 나가는데, 고치는 도중의
      값은 아직 형식이 안 맞을 수 있다 — 보내는 사람 주소를 고칠 때 `…@cre` 에서 손을 멈추면
      서버 스키마가 400 을 내고 화면에 「저장 실패」 가 뜬다(2026-09-28 실측).
      `lastSaved` 를 갱신하지 않고 물러나므로, 형식이 맞아지는 순간 그 값이 나간다.
    */
    if (!outreachDraftContentSchema.safeParse(content).success) return;

    /*
      서버에 없는 첫 값은 기다리지 않고 보낸다. 타이핑이 아니라 묶을 입력이 없고, 기다리는 사이
      「다음」 을 누르면 작성기가 내려가며 타이머가 지워져 확인 단계가 빈 줄을 읽는다.
    */
    if (lastSaved.current === null) {
      lastSaved.current = serialized;
      mutate({ country, content });
      return;
    }

    const timer = setTimeout(() => {
      lastSaved.current = serialized;
      mutate({ country, content });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [options.enabled, options.persistInitial, country, content, mutate]);

  return save;
}
